import { Box, CircularProgress, Typography } from '@mui/material';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { Procedure } from 'interfaces/procedure';
import { flatMap, size } from 'lodash';
import { DEFAULT_PAGE_SIZE } from 'utils/useEncodedFilters/constants';
import { DataGridInterfaceWrapper } from '../ProceduresPage/DataGrids/DataGridInterfaceWrapper';
import { getPageCasesInSlidesMode } from './casePagination';

interface NotEditableSlidesDataGridProps {
  procedures: Procedure[];
  isLoading?: boolean;
}

const NotEditableSlidesDataGrid: FunctionComponent<React.PropsWithChildren<NotEditableSlidesDataGridProps>> = ({
  procedures,
  isLoading = false,
}) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

  const totalSlides = size(flatMap(procedures, (procedure) => procedure.slides));
  const totalPages = Math.max(1, Math.ceil(totalSlides / rowsPerPage));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageCases = useMemo(
    () => getPageCasesInSlidesMode(procedures, page, rowsPerPage),
    [procedures, page, rowsPerPage]
  );
  const shouldShowLoadingOverlay = isLoading && totalSlides === 0;

  const onPageChange = (withOffset: boolean) => (event: React.ChangeEvent<unknown>, nextPage?: number) => {
    const nextPageWithOffset = withOffset ? (nextPage ?? 0) + 1 : nextPage ?? 1;
    if (page !== nextPageWithOffset) {
      setPage(nextPageWithOffset);
    }
  };

  const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newPageSize = parseInt(event.target.value) || DEFAULT_PAGE_SIZE;
    if (rowsPerPage !== newPageSize) {
      setRowsPerPage(newPageSize);
      setPage(1);
    }
  };

  return (
    <Box sx={{ position: 'relative', height: 650, width: '100%' }}>
      <DataGridInterfaceWrapper
        forceHeight="100%"
        casesInPage={pageCases}
        totalSlides={totalSlides}
        totalCases={procedures.length}
        isLoading={isLoading}
        disableEditing
        pendingSlidesMode={false}
        hideActionsMenu
        hideLinksToViewer
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        forcePage={page}
        forcePageSize={rowsPerPage}
        hideCaseIdColumn
      />
      {shouldShowLoadingOverlay && (
        <Box
          data-testid="not-editable-slides-loading-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.78)',
            pointerEvents: 'none',
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading lost and found slides...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NotEditableSlidesDataGrid;
